import {
  ClientReportDetailSectionDocument,
  ClientReportDetailSectionQuery,
  SaveReportAttachment_Input,
  SaveReportAttachmentMutation,
  useSaveReportAttachmentMutation
} from 'generated/graphql';
import _ from 'lodash';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

type IFormState = {
  clientReportId: string;
  apiFileId: string;
};

// THIS SHOULD NOT BE CHANGED WITHOUT CONSIDERATION.
// type IStandardAttachment = {
//   id: string;
//   fileName: string;
//   formattedFileSize: string;
// };

const initialState: IFormState = {
  clientReportId: '',
  apiFileId: ''
};

export function useFormState(reportId: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      clientReportId: {
        label: 'clientReportId',
        type: 'text'
      },
      apiFileId: {
        label: 'Upload file',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (formState.clientReportId === '' && reportId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'clientReportId',
          value: reportId
        }
      });
    }
    return () => {};
  });

  return React.useMemo(
    () => {
      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveReportAttachment() {
  const [
    fnMutate,
    tuple
  ] = useSaveReportAttachmentMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SaveReportAttachment_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<ClientReportDetailSectionQuery, SaveReportAttachmentMutation>(ClientReportDetailSectionDocument, (prevState, { mutationResult, queryVariables }) => {
            if (!prevState.clientReport) {
              return prevState;
            }
            if (!mutationResult.data?.saveClientReportAttachment?.id) {
              return prevState;
            }

            const isNew = queryVariables.id !== mutationResult.data.saveClientReportAttachment.id;
            if (isNew) {
              return {
                ...prevState,
                clientReport: {
                  ...prevState.clientReport,
                  attachments: _.compact([
                    ...prevState.clientReport.attachments,
                    mutationResult.data.saveClientReportAttachment
                  ])
                }
              };
            }
            const updatedAttachments = prevState.clientReport.attachments.map((node) => {
              if (node.id === mutationResult.data?.saveClientReportAttachment?.id) {
                return {
                  ...node,
                  ...mutationResult.data.saveClientReportAttachment
                };
              }
              return node;
            });

            return {
              ...prevState,
              clientReport: {
                ...prevState.clientReport,
                attachments: updatedAttachments
              }
            };
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
